import React, { FC, useState } from 'react';
import classNames from 'classnames';
import { useTheme } from '@mui/material/styles';
import { Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ReactMarkdown from 'react-markdown';
import moment from 'moment';
import { toast } from 'react-toastify';

import PCIcon from '@assets/pc.svg';
import VRIcon from '@assets/vr.svg';
import TabletIcon from '@assets/tablet.svg';
import ARIcon from '@assets/ar.svg';
import { ThemeProps } from '@styles/theme';
import { DATE_MMM_DD_YYYY } from '@shared/constants';
import { ToastMessage } from '@shared/components/Toast';
import { Button } from '@shared/components/Button';
import { ExpandButton } from '@shared/components/Button/ExpandButton';
import { useDownloadablesUIStore } from '@core/useStores';
import { messages } from 'Download/download.messages';
import { DownloadableProps } from 'Download/download.types';

import { PlatformBadge } from '../PlatformBadge';
import { VersionRow } from '../VersionRow';
import { useStyles } from './DownloadCard.styles';

const MIN_DESCRIPTION_LENGTH = 100;

const formatDate = (value: string | null) =>
    value ? moment(value.replace('T23:59:59Z', 'T12:00:00Z')).format(DATE_MMM_DD_YYYY) : '';

// Heuristic: pick a generic device-category icon from the download/group name.
const TYPE_ICON_RULES: { test: RegExp, icon: string }[] = [
    { test: /(handset|mobile|phone|android|ios|iphone|ipad|tablet)/, icon: TabletIcon },
    { test: /(headset|\bvr\b|\bxr\b|oculus|quest|vive|hololens|immersive|player)/, icon: VRIcon },
    { test: /(\bar\b|augmented)/, icon: ARIcon },
    { test: /(desktop|\bpc\b|windows|laptop|computer|\bmac\b|osx|editor|\bsdk\b|creator|manager|demo|unity|standalone)/, icon: PCIcon },
];

const getTypeIcon = (text: string): string | null =>
    TYPE_ICON_RULES.find(rule => rule.test.test(text.toLowerCase()))?.icon ?? null;

type DownloadCardProps = {
    downloadable: DownloadableProps,
    groupName?: string,
};

export const DownloadCard: FC<DownloadCardProps> = ({ downloadable, groupName }) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const downloadablesUIStore = useDownloadablesUIStore();
    const [expanded, setExpanded] = useState(false);

    const {
        name, description, platformLast, versionLast, dateLast,
        idLast, fileNameLast, releaseNotesLast, olderDownloadableVersionFile,
    } = downloadable;

    const hasFile = !!fileNameLast && fileNameLast !== 'empty';
    const olderVersions = olderDownloadableVersionFile ?? [];
    const hasOlderVersions = olderVersions.length > 0;
    const hasLongDescription = !!description && description.length > MIN_DESCRIPTION_LENGTH;
    const isExpandable = hasLongDescription || !!releaseNotesLast || hasOlderVersions;
    const date = formatDate(dateLast ?? null);
    const typeIcon = getTypeIcon(`${name} ${groupName ?? ''}`);
    const latestLabel = versionLast
        ? `${messages['downloadPage.card.latest']} ${versionLast}`
        : messages['downloadPage.card.latest'];

    const onDownload = () => {
        if (hasFile) {
            const toastId = toast.success(
                <ToastMessage message={'Downloading ' + fileNameLast + '...'} type='success' />,
            );
            downloadablesUIStore.downloadDownloadable(idLast as string, fileNameLast as string, toastId);
        } else {
            toast.error(
                <ToastMessage message={'Missing file'} type='error' />,
                { autoClose: 1000 },
            );
        }
    };

    const toggleExpanded = () => {
        if (isExpandable) {
            setExpanded(prev => !prev);
        }
    };

    return (
        <div className={classNames(styles.card, { [styles.expanded]: expanded })}>
            <div
                className={styles.header}
                onClick={toggleExpanded}
                style={isExpandable ? undefined : { cursor: 'default' }}
            >
                <div className={styles.typeIcon}>
                    {typeIcon
                        ? <img src={typeIcon} alt='' className={styles.typeIconImg} />
                        : <Inventory2OutlinedIcon className={styles.typeIconFallback} />}
                </div>

                <div className={styles.main}>
                    <p className={styles.name} title={name}>{name}</p>
                    {description && <p className={styles.description}>{description}</p>}
                    <div className={styles.metaCluster}>
                        {platformLast
                            ? <PlatformBadge platform={platformLast} size='small' />
                            : <span className={styles.noInfo}>{messages['downloadPage.noInfo']}</span>}
                        <span className={styles.latestChip}>{latestLabel}</span>
                        {date && <span className={styles.date}>{date}</span>}
                    </div>
                </div>

                <div className={styles.actions} onClick={e => e.stopPropagation()}>
                    {hasFile
                        ? (
                            <Button
                                className={styles.downloadButton}
                                onClick={onDownload}
                                startIcon={<FileDownloadOutlinedIcon />}
                            >
                                {messages['downloadPage.download']}
                            </Button>
                        )
                        : <span className={styles.noFile}>{messages['downloadPage.card.noFile']}</span>}

                    {isExpandable && (
                        <ExpandButton
                            expand={expanded}
                            onClick={toggleExpanded}
                            aria-label={name}
                        >
                            <ExpandMoreIcon />
                        </ExpandButton>
                    )}
                </div>
            </div>

            {isExpandable && (
                <Collapse in={expanded} timeout='auto' unmountOnExit>
                    <div className={styles.body}>
                        {hasLongDescription && (
                            <div className={styles.section}>
                                <h5 className={styles.sectionHeader}>{messages['downloadPage.subRow.description']}</h5>
                                <div className={styles.text}>{description}</div>
                            </div>
                        )}

                        <div className={styles.section}>
                            <h5 className={styles.sectionHeader}>{messages['downloadPage.subRow.releaseNotes']}</h5>
                            {releaseNotesLast
                                ? <ReactMarkdown className={styles.markdown}>{releaseNotesLast}</ReactMarkdown>
                                : <div className={styles.noInfo}>{messages['downloadPage.noInfo']}</div>}
                        </div>

                        {hasOlderVersions && (
                            <div className={styles.section}>
                                <div className={styles.sectionHeaderRow}>
                                    <h5 className={styles.sectionHeader}>{messages['downloadPage.subRow.olderVersions']}</h5>
                                    <span className={styles.versionsCount}>
                                        {messages['downloadPage.card.versionsCount'](olderVersions.length)}
                                    </span>
                                </div>
                                <div className={styles.historyList}>
                                    {olderVersions.map(file => <VersionRow key={file.id} file={file} />)}
                                </div>
                            </div>
                        )}
                    </div>
                </Collapse>
            )}
        </div>
    );
};

DownloadCard.displayName = 'DownloadCard';
